import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback } from 'react';
import { createDefaultActionBarHotkeys, type ActionBarHotkeyTarget } from '@sorrem/shared';
import { applyOptimistic, sorremApi, useAppDispatch, useGetCharactersQuery, type RootState } from '@sorrem-client/ui/store';
import { useCurrentCharacterArgs } from './use-current-character-id';

const DEFAULT_TARGETS: ActionBarHotkeyTarget[] = createDefaultActionBarHotkeys();

interface ActionBarTargetsState {
    /** What each action-bar slot triggers; the defaults (Draw in the last slot) until the character loads. */
    targets: readonly ActionBarHotkeyTarget[];
    /** Saves a full replacement mapping to the character (applied at once, undone if the server refuses). */
    setTargets: (targets: ActionBarHotkeyTarget[]) => void;
}

/**
 * What each action-bar slot triggers for the current character (each
 * character keeps its own): a carried slot, a skill, or nothing. Which *key*
 * presses a slot is an account-wide input binding instead; see
 * `useInputBindings`.
 */
export function useActionBarTargets(): ActionBarTargetsState {
    const args = useCurrentCharacterArgs();
    const dispatch = useAppDispatch();
    const { data: characters } = useGetCharactersQuery(args === skipToken ? skipToken : args.accountId);
    const targets = (args === skipToken ? undefined : characters?.find((character) => character.id === args.characterId))?.actionBarHotkeys ?? DEFAULT_TARGETS;

    const setTargets = useCallback((next: ActionBarHotkeyTarget[]): void => {
        if (args === skipToken) {
            return;
        }
        const { accountId, characterId } = args;
        dispatch((_dispatch, getState) => {
            const cached = sorremApi.endpoints.getCharacters.select(accountId)(getState() as RootState).data;
            // Before the character loads nothing is saved, so a bar built on the defaults never overwrites its own.
            if (!cached?.some((character) => character.id === characterId)) {
                return;
            }
            // Into the cache now, not when the request starts a tick later, so the next change builds on it.
            const applied = dispatch(sorremApi.util.updateQueryData('getCharacters', accountId, (draft) => {
                const character = draft.find((entry) => entry.id === characterId);
                if (character !== undefined) {
                    character.actionBarHotkeys = next;
                }
            }));
            void applyOptimistic(applied, dispatch(sorremApi.endpoints.putCharacterActionBarHotkeys.initiate({ accountId, characterId, body: { actionBarHotkeys: next } })).unwrap());
        });
    }, [args, dispatch]);

    return { targets, setTargets };
}
